import as from 'ansis'
import { Command } from 'commander'
import { z } from 'zod'

import { BaseCollections } from '@scalar-org/entities/database'
import { teamSchema } from '@scalar-org/entities/team'
import { namespaceSchema } from '@scalar-org/entities/utility'

import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'

export const ListSchemaCommand = () => {
  const cmd = new Command('list')

  cmd.description('List all schemas for a team namespace')

  // ---------------------------------------------------------------------------
  // Options
  cmd.option('--namespace <namespace>', 'Team namespace')

  cmd.action(async (args) => {
    const { error, data } = z
      .object({
        namespace: namespaceSchema.optional(),
      })
      .safeParse({
        namespace: args.namespace,
      })

    if (error)
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')

    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const client = await getDatabaseClient()

    const team = await client.getOne({
      collectionPath: BaseCollections.Teams,
      query: {
        uid: auth.teamUid,
      },
      schema: teamSchema,
    })

    if (team.error)
      return output
        .error()
        .title('An error occurred when fetching your team.')
        .message(team.message)
        .exit('error')

    const namespace = data.namespace || team.data.namespaces.at(0)

    if (!namespace) {
      return output
        .error()
        .title('Missing namespace')
        .message(
          'Could not find a valid namespace for your API. Please specify a namespace or contact support.',
        )
        .exit('error')
    }

    const apiClient = await apiService()

    const schemas = await apiClient.managedSchemas.getSchemas(namespace)

    if (schemas.error)
      return output.error().title(schemas.message).exit('error')

    // Display the list of APIs for a namespace
    output
      .info()
      .table(
        [
          [
            as.underline.bold('Slug'),
            as.underline.bold('Title'),
            as.underline.bold('Version'),
          ],
          ...schemas.data.map((schema) => {
            return [
              as.blue(schema.slug),
              as.blue(schema.title),
              schema.isPrivate ? as.green('Private') : as.yellow('Public'),
            ]
          }),
        ],
        { padding: 2 },
      )
      .print()
  })

  return cmd
}
